import useSuccessToast, { showErrorToast } from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";

import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import Spin from "@components/UI/Spin";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { TrashIcon } from "@components/Icons";
import { useFetchData } from "@hooks/useFetchData";
import { OutlineButton } from "@components/Buttons";
import { ColorBox } from "@components/LicensePolicy";
import { useQueryClient } from "@tanstack/react-query";
import useExportFile from "./useExportFile";
import useImportFile from "./useImportFile";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { DEFAULT_LICENSE_POLICY } from "@constants/index";

const TEST_ID = "license-policy-list";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const itemTestId = (licensePolicySeq) => tid(`item-${licensePolicySeq}`);

const LicensePolicyHeader = ({ onSearch, onExport, onAddManually, onImportFromFile, colors, isSearching = false, isImporting = false }) => {
  const { t } = useTranslation();
  const { hasEditPermission } = useRoutePermissions();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const fileInputRef = useRef(null);

  const handleSearch = (e) => {
    if (onSearch) {
      onSearch({ policyName: e.target.value });
    }
  };

  const toggleDropdown = () => {
    if (isImporting) return;
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleAddManually = () => {
    if (isImporting) return;
    setIsDropdownOpen(false);
    if (onAddManually) {
      onAddManually();
    }
  };

  const handleImportFromFile = () => {
    if (isImporting) return;
    setIsDropdownOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isImporting) {
      setIsDropdownOpen(false);
    }
  }, [isImporting]);

  return (
    <div className="flex flex-row space-y-0 items-center justify-between" data-testid={tid("header-controls")}>
      {/* Hidden file input for import */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".xlsx,.xls"
        onChange={(e) => {
          if (onImportFromFile) {
            onImportFromFile(e);
          }
        }}
        data-testid={tid("file-input")}
      />

      {/* Left side - Search */}
      <div className="relative" data-testid={tid("search-container")}>
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg className={`h-5 w-5 ${colors.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          data-testid={tid("search-input")}
          placeholder={t("common.search-placeholder")}
          onChange={handleSearch}
          className={`block border py-2 pr-3 pl-10 h-9 w-96 ${colors.borderPrimary} rounded-md leading-5 ${colors.bgSecondary} ${colors.textPrimary} ${colors.borderHoverPrimary} ${colors.textActive} outline-none`}
        />
        {isSearching && (
          <div className="absolute top-1/2 right-3 -translate-y-1/2 transform" data-testid={tid("search-spinner")}>
            <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.textActive}`}></div>
          </div>
        )}
      </div>

      {/* Right side - Export and Add buttons */}
      {hasEditPermission && (
        <div className="flex space-x-3 flex-row" data-testid={tid("action-buttons")}>
          {/* Export Button */}
          <OutlineButton onClick={onExport} disabled={isImporting} data-testid={tid("export-button")}>
            {t("common.export-button")}
          </OutlineButton>

          {/* Add New Policy Dropdown */}
          <div className="relative dropdown-container" data-testid={tid("add-dropdown")}>
            <button
              type="button"
              data-testid={tid("add-button")}
              onClick={toggleDropdown}
              disabled={isImporting}
              className={`font-600 inline-flex items-center justify-center rounded-md border-transparent px-4 py-2 h-9 text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isImporting ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {isImporting && <Spin className="mr-2" data-testid={tid("import-spinner")} />}
              <span>{isImporting ? t("common.importing") : t("license-policy.add-new-policy")}</span>
              {!isImporting && (
                <svg
                  className={`ml-2 h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && !isImporting && (
              <div 
                className="absolute right-0 z-[9999] mt-1 w-48 rounded-[0.25rem] border-t-0 p-[0.125rem]"
                data-testid={tid("add-dropdown-menu")}
                style={{
                  backgroundColor: colors.bgPrimaryColor,
                  boxShadow: "0px 0px 6px 1px rgba(0, 0, 0, 0.6)"
                }}
              >
                <div 
                  className="rounded-[0.5rem] p-2 m-0"
                  style={{
                    backgroundColor: colors.bgPrimaryColor,
                    color: colors.textPrimaryColor
                  }}
                >
                  <button
                    type="button"
                    data-testid={tid("add-manually-button")}
                    onClick={handleAddManually}
                    onMouseEnter={() => setHoveredOption('manual')}
                    onMouseLeave={() => setHoveredOption(null)}
                    className="flex w-full items-center text-left text-sm transition-all duration-200 relative rounded-[0.25rem] px-3 py-2 mb-[3px]"
                    style={{
                      backgroundColor: colors.bgPrimaryColor,
                      color: hoveredOption === 'manual' ? colors.textActiveColor : colors.textPrimaryColor,
                      border: hoveredOption === 'manual' ? `1px solid ${colors.dropdownBorderColor}` : "1px solid transparent",
                      boxShadow: hoveredOption === 'manual' ? "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)" : "none",
                      zIndex: hoveredOption === 'manual' ? 1000 : 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    <span className="font-medium">{t("license-policy.add-manually")}</span>
                  </button>
                  <button
                    type="button"
                    data-testid={tid("import-from-file-button")}
                    onClick={handleImportFromFile}
                    onMouseEnter={() => setHoveredOption('import')}
                    onMouseLeave={() => setHoveredOption(null)}
                    className="flex w-full items-center text-left text-sm transition-all duration-200 relative rounded-[0.25rem] px-3 py-2"
                    style={{
                      backgroundColor: colors.bgPrimaryColor,
                      color: hoveredOption === 'import' ? colors.textActiveColor : colors.textPrimaryColor,
                      border: hoveredOption === 'import' ? `1px solid ${colors.dropdownBorderColor}` : "1px solid transparent",
                      boxShadow: hoveredOption === 'import' ? "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)" : "none",
                      zIndex: hoveredOption === 'import' ? 1000 : 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    <span className="font-medium">{t("license-policy.import-from-file")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// LicensePolicyItem Component
const LicensePolicyItem = ({ policy, onDelete, colors }) => {
  const { t } = useTranslation();
  const { hasDeletePermission } = useRoutePermissions();
  const navigate = useNavigate();

  const renderStepColors = (licensePolicySteps) => {
    if (!licensePolicySteps || licensePolicySteps.length === 0) {
      return <span className={colors.textNeutralMuted}>{ t("license-policy.no-steps-defined") }</span>;
    }

    // Sort steps by policyStep to ensure correct order
    const sortedSteps = [...licensePolicySteps].sort((a, b) => a.policyStep - b.policyStep);

    const setStepTooltipContent = (step) => {
      return t("license-policy.step-tooltip-content", {
        policyStep: step.policyStep,
        licenseUseApprovalCdName: step.licenseUseApprovalCdName,
        remark: step.remark ? `- ${step.remark}` : ''
      })
    }

    return (
      <div className="flex items-center gap-1">
        {sortedSteps.map((step) => (
          <div
            key={step.licensePolicyStepSeq}
            className="mr-3 relative"
            title={ setStepTooltipContent(step) }
          >
            <ColorBox
              colorName={step.policyStepColor}
              size="lg"
              showLabel={false}
            />
            <div className={`absolute inset-0 flex items-center justify-center text-sm font-bold ${colors.textStaticWhite} pointer-events-none`}>
              {step.policyStep}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const canDelete = hasDeletePermission && policy.policyName !== DEFAULT_LICENSE_POLICY.POLICY_NAME;

  return (
    <div
      className={`relative ${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary} hover:shadow-lg transition-shadow duration-200 cursor-pointer`}
      onClick={() => navigate(`/license-policy/${policy.licensePolicySeq}`)}
      data-testid={itemTestId(policy.licensePolicySeq)}
    >
      {canDelete && (
        <button
          type="button"
          data-testid={tid(`item-${policy.licensePolicySeq}-delete-button`)}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(policy); }}
          className={`absolute top-6 right-6 z-10 p-2 rounded-lg ${colors.hoverBgDangerSoft} transition-colors duration-200`}
          title={t("license-policy.delete-policy-tooltip")}
        >
          <TrashIcon className={`w-4 h-4 ${colors.iconDelete}`} />
        </button>
      )}

      {/* Policy Title */}
      <h3
        className={`text-md font-medium ${colors.textPrimary} mb-4 ${canDelete ? "pr-10" : ""}`}
        data-testid={tid(`item-${policy.licensePolicySeq}-name`)}
      >
        {policy.policyName}
      </h3>

      {/* Single row layout for Step Colors, Global Policy, and Remark */}
      <div className="grid grid-cols-[280px_150px_1fr] gap-6 items-start" data-testid={tid(`item-${policy.licensePolicySeq}-details`)}>
        {/* Step Colors */}
        <div data-testid={tid(`item-${policy.licensePolicySeq}-obligation-colors`)}>
          <span className={`text-sm ${colors.textSecondary} font-medium block mb-2 uppercase`}>
            {t("license-policy.obligation-type-color")}
          </span>
          {renderStepColors(policy.licensePolicySteps)}
        </div>

        {/* Global Policy */}
        <div data-testid={tid(`item-${policy.licensePolicySeq}-default-policy`)}>
          <span className={`text-sm ${colors.textSecondary} font-medium block mb-2 uppercase`} data-testid={tid(`item-${policy.licensePolicySeq}-default-policy-label`)}>
            {t("license-policy.default-policy")}
          </span>
          <span className={`text-sm font-medium ${colors.textPrimary}`} data-testid={tid(`item-${policy.licensePolicySeq}-default-policy-value`)}>
            {policy.globalFlag === 'Y' ? t("common.yes") : t("common.no")}
          </span>
        </div>

        {/* Remark */}
        <div className="min-w-0 overflow-hidden" data-testid={tid(`item-${policy.licensePolicySeq}-remark`)}>
          <span className={`text-sm ${colors.textSecondary} font-medium block mb-2 uppercase`}>
            {t("license-policy.remark")}
          </span>
          <p
            className={`text-sm ${colors.textPrimary} min-w-0 w-full truncate`}
            title={policy.remark || "---"}
          >
            {policy.remark || "---"}
          </p>
        </div>
      </div>
    </div>
  );
};

const LicensePolicyList = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { exportFile } = useExportFile();
  const { importFile } = useImportFile();
  const queryClient = useQueryClient();

  const [searchFilters, setSearchFilters] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const navigate = useNavigate();

  const { data: licensePolicyList, isLoading, error, refetch } = useFetchData(
    [queryKeys.getListLicensePolicy, searchFilters],
    queryKeys.getListLicensePolicy,
    { method: "GET" },
    searchFilters,
    {},
  );

  // Controls
  const handleExport = async () => {
    try {
      await exportFile();
      useSuccessToast(TOAST.success, t("toast.license-policy.export-success"));
    } catch (error) {
      console.error("Error exporting license policies:", error);
      showErrorToast(t("toast.license-policy.export-success"))
    }
  };

  const handleAddManually = () => {
    navigate("/license-policy/new");
  };

  const handleImportFromFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const result = await importFile(file);
      queryClient.invalidateQueries([queryKeys.getListLicensePolicy]);
      useSuccessToast(TOAST.success, t("toast.license-policy.import-success"));
    } catch (error) {
      console.error("Error importing file:", error);
    } finally {
      setIsImporting(false);
      // Reset the file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Debounced search handler
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (inputData) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          // Extract the search value from the input data
          const searchValue = inputData.policyName || "";

          // Update search filters
          if (searchValue.trim() === "") {
            setSearchFilters({});
          } else {
            setSearchFilters({ policyName: searchValue.trim() });
          }
        }, 300); // 300ms delay
      };
    })(),
    []
  );

  // Search handler
  const searchHandler = debouncedSearch;

  const deleteLicensePolicy = useMutationData(
    (licensePolicySeq) => queryKeys.deleteLicensePolicy + "/" + licensePolicySeq,
    { method: "DELETE" },
    {
      onSuccess: () => {
        useSuccessToast(TOAST.success, t("license-policy.license-policy-deleted-success"));
        setSelectedPolicy(null);
        refetch();
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        const errorMessage = error?.response?.data;
        const messageText =
          typeof errorMessage === "string"
            ? errorMessage
            : errorMessage?.message ?? "";
        const isDefaultPolicyError = messageText.includes(
          "Default Policy cannot be deleted until another policy is designated as the new default."
        );
        if (error.response?.status === 409) {
          showErrorToast(
            t("status.error"),
            isDefaultPolicyError
              ? t("license-policy.license-policy-deleted-error-default-policy")
              : t("license-policy.license-policy-deleted-error-conflict")
          );
        } else {
          showErrorToast(t("status.error"), t("license-policy.license-policy-deleted-error"));
        }
      },
    },
  );

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteLicensePolicy.mutate(selectedPolicy.licensePolicySeq);
      setSelectedPolicy(null);
      setShowModalDelete(false);
      // Note: The data will be refreshed automatically by the useFetchData hook
    } catch (error) {
      console.error("Error deleting license policies:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePolicyDelete = (policy) => {
    setSelectedPolicy(policy);
    setShowModalDelete(true);
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header */}
      <div className="py-4" data-testid={tid("header")}>
        <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`} data-testid={tid("title")}>
          {t("license-policy.license-policy-list")}
        </h1>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pb-4`} data-testid={tid("controls")}>
        <LicensePolicyHeader
          onSearch={searchHandler}
          onExport={handleExport}
          onAddManually={handleAddManually}
          onImportFromFile={handleImportFromFile}
          colors={colors}
          isSearching={isLoading}
          isImporting={isImporting}
        />
      </div>

      {/* Policy List */}
      <div className="flex-grow overflow-y-auto pb-6" data-testid={tid("list")}>
        {isLoading ? (
          // Loading skeleton
          <div className="space-y-4" data-testid={tid("loading")}>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className={`${colors.bgPrimary} rounded-lg p-6 animate-pulse border ${colors.borderPrimary}`} data-testid={tid(`loading-skeleton-${index}`)}>
                <div className={`h-6 ${colors.bgSkeletonDark} rounded mb-4 w-3/4`}></div>
                <div className={`h-4 ${colors.bgSkeletonDark} rounded mb-3 w-1/2`}></div>
                <div className={`h-4 ${colors.bgSkeletonDark} rounded mb-3 w-1/3`}></div>
                <div className={`h-4 ${colors.bgSkeletonDark} rounded w-2/3`}></div>
              </div>
            ))}
          </div>
        ) : error ? (
          // Error state
          <div className={`${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary} text-center`} data-testid={tid("error")}>
            <p className={`${colors.textDanger} mb-2`} data-testid={tid("error-message")}>{ t("license-policy.error-loading-message") }</p>
            <p className={`${colors.textSecondary} text-sm`} data-testid={tid("error-detail")}>{error.message || 'Please try again later'}</p>
          </div>
        ) : licensePolicyList.length === 0 ? (
          // Empty state
          <div className={`${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary} text-center`} data-testid={tid("empty")}>
            <p className={`${colors.textSecondary} mb-2`} data-testid={tid("empty-message")}>{ t("license-policy.no-license-message") }</p>
            <p className={`${colors.textSecondary} text-sm`} data-testid={tid("empty-hint")}>{ t("license-policy.create-first-policy-message") }</p>
          </div>
        ) : (
          // Policy list
          <div className="space-y-4" data-testid={tid("items")}>
            {licensePolicyList.map((policy) => (
              <LicensePolicyItem
                key={policy.licensePolicySeq}
                policy={policy}
                onDelete={handlePolicyDelete}
                colors={colors}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {selectedPolicy && (
        <ConfirmDeleteModal
          data-testid={tid("delete-modal")}
          isOpen={showModalDelete}
          onClose={() => setShowModalDelete(false)}
          onConfirm={handleConfirmDelete}
          title={t("license-policy.confirm-delete-title")}
          description={t("license-policy.confirm-delete-body")}
          cancelText={t("common.cancel-button")}
          confirmText={t("common.delete-button")}
          loading={deleteLoading}
        />
      )}
    </div>
  );
};

export default LicensePolicyList;
